import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { isPatient } from 'utils/userTypeHelper';
import format from 'date-fns/format';
import CommentForm from 'components/molecules/commentForm';
import { getActivityThresholdsValues } from 'utils';
import ActivityStatus from 'components/atoms/activityStatus';
import ActivityComments from 'components/molecules/comments';
import ActivityCheckBox from 'components/atoms/checkbox';
import common from 'constants/common';
import useUser from 'utils/useUser';
import NoteIcon from 'components/atoms/icons/noteIcon';
import ReadingItem from 'components/atoms/readingItem';

const ActivityRow = ({
  columnNumber,
  activity,
  thresholds,
  reviewed,
  handleCommentSubmit,
  handleReviewedCheck,
  setReviewedLoading,
  submitCommentLoading,
  disabledModalButton,
  openModal,
}) => {
  const { userType } = useUser();
  const [commentsForm, setCommentsForm] = useState(() => {});
  const [comment, setComment] = useState(null);
  const [modalValues, setModalValues] = useState({});
  const {
    doctor: { addComment, removeComment },
  } = useDispatch();

  const commentChanged = (value, props) => {
    setComment(value);
    setCommentsForm(props);
    if (value) addComment(activity.id);
    else removeComment(activity.id);
  };

  const commentSubmitted = (values, { resetForm }) => {
    handleCommentSubmit(activity.id, activity.deviceId, values, resetForm);
    setComment(null);
    setModalValues({});
    removeComment(activity.id);
  };

  return (
    <tr className="activity-row">
      <td data-label="№">{columnNumber}</td>
      <td data-label="Status" className="status-row">
        <ActivityStatus status={activity.globalStatus} />
      </td>
      <td data-label="Time">
        <span>
          R:{' '}
          {format(
            new Date(parseInt(activity.createdAt)),
            common.dateFormats.dayOfWeek,
          )}
        </span>
        <span>
          {format(
            new Date(parseInt(activity.createdAt)),
            common.dateFormats.timeAPM,
          )}
        </span>
      </td>
      <td data-label="Reading">
        <ReadingItem activity={activity} />
        {getActivityThresholdsValues(
          activity.globalStatus,
          activity.value,
          activity.statuses,
          thresholds,
          activity.readingType,
        ).map((threshold) => (
          <span key={threshold}>{`(${threshold})`}</span>
        ))}
      </td>
      {!isPatient(userType) && (
        <>
          <td data-label="Comment" className="rpm-comment">
            <ActivityComments
              comments={activity.comments}
              patientGuid={activity.patient.guid}
            />
            <CommentForm
              minified={activity.comments.length !== 0}
              valueChanged={commentChanged}
              handleSubmit={commentSubmitted}
              disabled={submitCommentLoading}
              activity={activity}
              comment={comment}
              hideCount
            />
            <div
              role="button"
              aria-hidden
              onClick={() =>
                openModal({
                  activity,
                  values: modalValues,
                  setValues: setModalValues,
                  handleSubmit: setComment,
                })
              }
              className="rpm-template-button"
            >
              <NoteIcon fill={disabledModalButton ? '#CCCAD7' : '#2C2543'} />
            </div>
          </td>
          {!reviewed && (
            <td data-label="Reviewed" className="status-row">
              <ActivityCheckBox
                handleCheck={async () => {
                  await handleReviewedCheck(activity.id, activity.deviceId);
                  if (comment && commentsForm) {
                    await handleCommentSubmit(
                      activity.id,
                      activity.deviceId,
                      { comment },
                      commentsForm.handleReset,
                    );
                    setComment(null);
                  }
                }}
                loading={!!(setReviewedLoading || activity.disabled)}
              />
            </td>
          )}
        </>
      )}
    </tr>
  );
};

ActivityRow.propTypes = {
  columnNumber: PropTypes.number,
  reviewed: PropTypes.bool.isRequired,
  activity: PropTypes.shape().isRequired,
  thresholds: PropTypes.shape().isRequired,
  handleCommentSubmit: PropTypes.func.isRequired,
  handleReviewedCheck: PropTypes.func.isRequired,
  submitCommentLoading: PropTypes.bool.isRequired,
  setReviewedLoading: PropTypes.bool.isRequired,
  disabledModalButton: PropTypes.bool,
  openModal: PropTypes.func,
};

export default ActivityRow;
